/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState, useEffect, type FunctionComponent } from 'react'

import { FormattedMessage } from '@modules/i18n'
import type { DefaultSolutionView } from '@modules/deployment-creation-wizard/types'
import DeploymentCreationWizardLayout from '@modules/deployment-creation-wizard/DeploymentCreationWizardLayout'
import { useSolutionPickerVersion } from '@modules/solution-selector/hooks'
import { useSolutionPreview } from '@modules/solution-selector/useSolutionPreview'

import { deploymentCreateCrumbs } from '@/lib/crumbBuilder'

import SolutionTypeAwareCreateStackDeploymentEditorWrapper from './SolutionTypeAwareCreateStackDeploymentEditorWrapper'
import DefaultSolutionViewSelector from './DefaultSolutionViewSelector'
import { toDefaultSolutionView } from './lib'
import useDeploymentCreationWizardSteps from './hooks'
import useEbtDeploymentCreationWizard from './use-ebt-deployment-creation-wizard'

const DeploymentCreationWizard: FunctionComponent = () => {
  const [currentStep, setCurrentStep, generateStepDetails] =
    useDeploymentCreationWizardSteps('solution-selector')

  const [defaultSolutionView, setDefaultSolutionView] = useState<DefaultSolutionView>('search')

  const { reportDeploymentUseCaseEbt } = useEbtDeploymentCreationWizard()
  const solutionPickerVersion = useSolutionPickerVersion()

  const { selectedSolution, setSelectedSolution, selectedImage } = useSolutionPreview()

  // Reset selectedSolution when returning to solution-selector step
  useEffect(() => {
    if (currentStep === 'solution-selector') {
      setSelectedSolution(null)
    }
  }, [currentStep, setSelectedSolution])

  const steps = generateStepDetails(currentStep)

  let title =
    solutionPickerVersion === 'v6' ? (
      <FormattedMessage
        defaultMessage='Which solution would you like to deploy?'
        id='deployment-creation-wizard.select-solution-view-v6'
      />
    ) : (
      <FormattedMessage
        defaultMessage='Select a solution view for your deployment'
        id='deployment-creation-wizard.select-solution-view'
      />
    )

  let description =
    solutionPickerVersion === 'v6' ? (
      <FormattedMessage
        defaultMessage='Choose between Elastic’s 3 solutions. Select to preview.'
        id='deployment-creation-wizard.solution-view-description-v6'
      />
    ) : (
      <Fragment>
        <FormattedMessage
          defaultMessage='Since version 8.16, hosted deployments offer lighter and solution-based navigations, called solution views.'
          id='deployment-creation-wizard.solution-view-description.1'
        />
        <br />
        <FormattedMessage
          defaultMessage='Admins of this deployment can change the solution view from space settings later.'
          id='deployment-creation-wizard.solution-view-description.2'
        />
      </Fragment>
    )

  let child = (
    <DefaultSolutionViewSelector
      key={currentStep} // Force re-mount when step changes
      onChooseSolution={(label) => {
        setCurrentStep(`deployment-editor`)
        setDefaultSolutionView(toDefaultSolutionView(label))
        reportDeploymentUseCaseEbt({
          answer: toDefaultSolutionView(label),
          resource: 'stateful',
          solutionVersion: solutionPickerVersion,
        })
      }}
      onSelectionChange={setSelectedSolution}
      solutionPickerVersion={solutionPickerVersion}
      title={solutionPickerVersion === 'v6' ? title : undefined}
      description={solutionPickerVersion === 'v6' ? description : undefined}
    />
  )

  if (currentStep === `deployment-editor`) {
    title = (
      <FormattedMessage
        defaultMessage='Confirm the settings of your deployment'
        id='deployment-creation-wizard.confirm-deployment-settings'
      />
    )

    description = (
      <FormattedMessage
        defaultMessage='Your data will be stored in the cloud provider of your choice.'
        id='deployment-creation-wizard.confirm-deployment-settings-description'
      />
    )

    child = (
      <SolutionTypeAwareCreateStackDeploymentEditorWrapper solutionView={defaultSolutionView} />
    )
  }

  return (
    <DeploymentCreationWizardLayout
      breadcrumbs={deploymentCreateCrumbs()}
      steps={steps}
      title={title}
      description={description}
      solutionPickerVersion={solutionPickerVersion}
      selectedSolution={selectedSolution}
      selectedImage={selectedImage}
      currentStep={currentStep}
    >
      {child}
    </DeploymentCreationWizardLayout>
  )
}

export default DeploymentCreationWizard
